// Qué se ofrece en «Elige el teléfono» (teléfonos y kits con un teléfono), qué kit va como extra y
// si la venta ya tiene teléfono. Puro, sin React.
import { categoryOf } from './cartLines';
import { isService, isSoldOut } from './catalogAvailability';
import type { CatalogItem, ReservedLine, WizardSession } from './types';

const isPhone = (item: Pick<CatalogItem, 'category' | 'itemType'>): boolean =>
    !isService(item) && categoryOf(item) === 'PHONE';

/** Filtro de tipo de «Elige el teléfono». */
export type PhoneOfferFilter = 'ALL' | 'PHONE' | 'BUNDLE';

export const PHONE_OFFER_FILTERS: { value: PhoneOfferFilter; label: string }[] = [
    { value: 'ALL', label: 'Todos' },
    { value: 'PHONE', label: 'Teléfonos' },
    { value: 'BUNDLE', label: 'Kits' },
];

/** Teléfonos que trae un kit; el catálogo no dice la categoría del componente, se busca por SKU. */
function kitPhoneCount(kit: CatalogItem, bySku: ReadonlyMap<string, CatalogItem>): number {
    return (kit.components ?? []).reduce((sum, c) => {
        const component = bySku.get(c.sku);
        return component !== undefined && isPhone(component) ? sum + c.qty : sum;
    }, 0);
}

/** Kit sin teléfono: no ocupa el lugar del teléfono y va como extra; un componente desconocido no cuenta. */
export function isAccessoryKit(item: CatalogItem, items: readonly CatalogItem[]): boolean {
    const components = item.components ?? [];
    if (categoryOf(item) !== 'BUNDLE' || components.length === 0) return false;
    const bySku = new Map(items.map((i) => [i.sku, i]));
    return components.every((c) => bySku.has(c.sku)) && kitPhoneCount(item, bySku) === 0;
}

/** No es teléfono ni lo trae; un kit entra solo si se sabe que no lleva teléfono. */
export function isPhoneFree(item: CatalogItem, items: readonly CatalogItem[]): boolean {
    if (isPhone(item)) return false;
    return categoryOf(item) !== 'BUNDLE' || isAccessoryKit(item, items);
}

/**
 * Teléfonos sueltos y kits con exactamente un teléfono, con existencia en la tienda. Con
 * `keepSoldOutPhones` también van los teléfonos agotados, para pintar sus variantes sin piezas.
 */
export function phoneOffersOf(
    items: CatalogItem[],
    filter: PhoneOfferFilter,
    { keepSoldOutPhones = false }: { keepSoldOutPhones?: boolean } = {},
): CatalogItem[] {
    const bySku = new Map(items.map((i) => [i.sku, i]));
    return items.filter((i) => {
        if (isService(i)) return false;
        const category = categoryOf(i);
        if (isSoldOut(i) && !(keepSoldOutPhones && category === 'PHONE')) return false;
        if (filter !== 'ALL' && category !== filter) return false;
        if (category === 'PHONE') return true;
        return category === 'BUNDLE' && kitPhoneCount(i, bySku) === 1;
    });
}

/** Teléfonos de la línea: el suelto o los componentes teléfono del kit. */
export function linePhoneCount(line: ReservedLine): number {
    if (line.components.length > 0) {
        return line.components.reduce((n, c) => (c.category === 'PHONE' ? n + c.qty : n), 0);
    }
    return (line.product?.category ?? 'PHONE') === 'PHONE' ? line.qty : 0;
}

/** Línea viva con teléfono (suelto o en kit); los regalos no cuentan. */
export function phoneLineOf(
    session: Pick<WizardSession, 'reservedLines'> | undefined,
): ReservedLine | undefined {
    return (session?.reservedLines ?? []).find(
        (l) => l.status !== 'RELEASING' && l.role !== 'GIFT' && linePhoneCount(l) > 0,
    );
}

/** La venta ya tiene teléfono o kit con teléfono; sin él no se pasa a Forma de pago (REQ-80). */
export function sessionHasPhone(
    session: Pick<WizardSession, 'reservedLines' | 'product'>,
): boolean {
    if (session.reservedLines === undefined) return session.product !== undefined;
    return phoneLineOf(session) !== undefined;
}
